import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { SelectPicker } from "rsuite";
import { Alert, Button, Input } from "reactstrap";

import { currentViewAtom } from "../../../atoms/ViewAtoms";
import AppEmptyState from "../../../elements/AppEmptyState";
import IconButton from "../../../elements/IconButton";
import ListItem from "../../CRM/ListItem";
import { ShareAuthType } from "../../PageView/ShareScreenDrawer/ShareScreenDrawer";
import RequiredTitle from "../../../elements/RequiredTitle";
import AppSpinner from "../../../elements/AppSpinner";
import useUserSharedRecordPage from "../../../api/api-hooks/sharedRecordsPage/useSharedRecordPage";
import useCreateSharedRecordPage from "../../../api/api-hooks/sharedRecordsPage/useCreateSharedRecordPage";
import useUpdateSharedRecordPage from "../../../api/api-hooks/sharedRecordsPage/useUpdateSharedRecordPage";
import useUnshareRecordPage from "../../../api/api-hooks/sharedRecordsPage/useUnshareRecordPage";
import { AppConfirm } from "../../../utils/PopupUtil";
import CopyGroup from "../../Modals/CopyGroup";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

const authOptions = [
  { label: "Password Protected", value: "Password" },
  { label: "Require Logged In User", value: "GC User" },
  { label: "None", value: "None" },
];

interface Props {
  recordID: string;
  recordModule: string;
  viewID?: string;
}

const ShareRecordPage = ({ recordID, recordModule, viewID }: Props) => {
  const currentViewID = useAtomValue(currentViewAtom)?.id || viewID;
  const tenantID = useAtomValue(tenantIDAtom);
  const [error, setError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [sharedSubViews, setShareSubViews] = useState<string[]>([]);
  const [auth, setAuth] = useState<ShareAuthType>();
  const [password, setPassword] = useState("");
  const {
    exists,
    sharedRecordPageID,
    authentication,
    password: existingPassword,
    subviews,
    view,
    recordPageLoading,
  } = useUserSharedRecordPage({ recordID, viewID: currentViewID ?? "" });
  const { createSharedRecordPage, creatingSharedRecordPageLoading } =
    useCreateSharedRecordPage();
  const { updateSharedRecordPage, updateSharedRecordPageLoading } =
    useUpdateSharedRecordPage();
  const { unshareRecordPage, unshareRecordPageLoading } =
    useUnshareRecordPage();

  useEffect(() => {
    setPassword(existingPassword ?? "");
    setAuth(authentication);
    setShareSubViews(subviews ?? []);
  }, [existingPassword, authentication, subviews]);

  const parentSubViews = useMemo(() => {
    if (!view?.SubViews) return [];

    return view?.SubViews.flatMap((sv) => {
      if (["Summary", "History"].includes(sv?.Type)) return [];

      return [sv];
    });
  }, [view?.SubViews]);

  const handleSubViews = (svID: string) => {
    if (sharedSubViews.includes(svID)) {
      const ssv = sharedSubViews.filter((sv) => sv !== svID);
      setShareSubViews(ssv);
    } else {
      setShareSubViews([...sharedSubViews, svID]);
    }
  };

  if (recordPageLoading) {
    return (
      <div className="h-100 w-100 d-flex justify-content-center align-items-center">
        <AppSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div>
        {exists && (
          <>
            <div className="mt--2">
              <CopyGroup
                name={`Shared Record Page Link`}
                description={`Copy and paste this link to share the record's information and views.`}
                link={`${window.location.origin}/shared-record-page/${tenantID}/${sharedRecordPageID}/Summary`}
              />
            </div>
            <hr />
          </>
        )}
        <RequiredTitle title={`Record Page Authentication`} />
        <SelectPicker
          data={authOptions}
          value={auth}
          block
          placeholder={`Select Record Page Authentication`}
          searchable={false}
          cleanable={false}
          onChange={(val) => setAuth(val as ShareAuthType)}
          className="pb-3"
        />
        {auth === "Password" && (
          <>
            <RequiredTitle title="Password" className="mt-2" />
            <Input
              bsSize="sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="mb-2"
            />
          </>
        )}
        {parentSubViews?.length ? (
          <>
            {parentSubViews.map((sv) => {
              return (
                <>
                  <ListItem
                    title={sv?.Name}
                    key={sv?.id}
                    icon={"mdi-newspaper-variant-outline"}
                    optionsButton={
                      <div className="d-flex">
                        <IconButton
                          onClick={() => handleSubViews(sv.id)}
                          icon={
                            sharedSubViews.includes(sv.id) ? "eye" : "eye-off"
                          }
                          tooltipText={
                            sharedSubViews.includes(sv.id) ? "Hide" : "Show"
                          }
                          className="mx-1"
                        />
                      </div>
                    }
                  />
                </>
              );
            })}
          </>
        ) : (
          <>
            <AppEmptyState
              title="SubViews not found."
              description="There are no subviews added to this page."
            />
          </>
        )}
        <div className="d-flex align-items-center mt-4">
          {exists && (
            <Button onClick={handleUnshareView}>
              {unshareRecordPageLoading ? (
                <AppSpinner color="primary" />
              ) : (
                `Unshare Record`
              )}
            </Button>
          )}
          <Button
            color="primary"
            style={{ width: 190 }}
            disabled={
              !auth ||
              (auth === "Password" && !password) ||
              updateSharedRecordPageLoading ||
              creatingSharedRecordPageLoading
            }
            onClick={
              exists
                ? handleUpdateSharedRecordPage
                : handleShareRecordPageCreation
            }
          >
            {updateSharedRecordPageLoading ||
            creatingSharedRecordPageLoading ? (
              <AppSpinner color="white" />
            ) : exists ? (
              `Update Settings`
            ) : (
              `Share Record Page`
            )}
          </Button>
        </div>
        {error && (
          <Alert className="mt-4" color="danger">
            {error}
          </Alert>
        )}
        {updateSuccess && (
          <Alert className="mt-4">
            Successfully updated shared record page settings!
          </Alert>
        )}
      </div>
    </div>
  );

  async function handleUpdateSharedRecordPage() {
    setError("");
    setUpdateSuccess(false);

    if (!currentViewID) return;

    updateSharedRecordPage({
      viewID: currentViewID,
      recordID,
      authentication: auth,
      password,
      subviews: sharedSubViews,
      onSuccess: () => setUpdateSuccess(true),
      onError: setError,
    });
  }

  function handleShareRecordPageCreation() {
    if (!auth || !currentViewID) return;

    setError("");
    setUpdateSuccess(false);

    createSharedRecordPage({
      viewID: currentViewID,
      authentication: auth,
      password: password,
      recordID: recordID,
      recordModule: recordModule,
      subviews: sharedSubViews,
      onSuccess: () => {},
      onError: setError,
    });
  }

  async function handleUnshareView() {
    const result = await AppConfirm({
      title: `Unshare Record`,
      text: `Are you sure you want to unshare this Record?`,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    setError("");
    setUpdateSuccess(false);

    if (!currentViewID) return;

    unshareRecordPage({
      viewID: currentViewID,
      recordID: recordID,
      onError: setError,
      onSuccess: () => {},
    });
  }
};

export default ShareRecordPage;
